/**
 * MagneticButton — CTA follows cursor slightly on desktop.
 * Uses motion values only (no motion.* component) — avoids framer-motion v12 warnings.
 */
import { useRef } from "react";
import { useMotionTemplate, useMotionValue, useSpring } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import useIsFinePointer from "@/lib/useIsFinePointer";

const SPRING = { stiffness: 220, damping: 18, mass: 0.3 };

export const MagneticButton = ({
  as: Tag = "button",
  strength = 0.28,
  maxOffset = 8,
  className,
  testId,
  children,
  ...rest
}) => {
  const fine = useIsFinePointer();
  const reduced = usePreferStaticUI();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, SPRING);
  const sy = useSpring(y, SPRING);
  const transform = useMotionTemplate`translate3d(${sx}px, ${sy}px, 0)`;

  const enabled = fine && !reduced;

  const onMove = (e) => {
    if (!enabled || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) * strength;
    const dy = (e.clientY - cy) * strength;
    x.set(Math.max(-maxOffset, Math.min(maxOffset, dx)));
    y.set(Math.max(-maxOffset, Math.min(maxOffset, dy)));
  };

  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  if (!enabled) {
    return (
      <Tag ref={ref} className={className} data-testid={testId} {...rest}>
        {children}
      </Tag>
    );
  }

  return (
    <span
      ref={ref}
      className="inline-flex"
      style={{ transform }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <Tag className={className} data-testid={testId} {...rest}>
        {children}
      </Tag>
    </span>
  );
};

export default MagneticButton;
